import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Checkbox,
  FormControlLabel,
  TextField,
  Grid,
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  DeleteOutline as DeleteOutlineIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import contactService, { ImportContactJob } from '../services/contactService';

interface ImportJobLogViewerProps {
  open: boolean;
  jobId: number | null;
  clientId: number;
  onClose: () => void;
}

const ImportJobLogViewer: React.FC<ImportJobLogViewerProps> = ({
  open,
  jobId,
  clientId,
  onClose,
}) => {
  const [job, setJob] = useState<ImportContactJob | null>(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(5);
  const [copied, setCopied] = useState(false);
  const logEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && jobId) {
      fetchJob();
      // Auto-refresh will be controlled by the status check in the other useEffect
    } else {
      setJob(null);
      setAutoRefresh(true);
    }
  }, [open, jobId]);

  // Disable auto-refresh for completed/failed/stopped jobs
  useEffect(() => {
    if (job) {
      if (job.status === 'completed' || job.status === 'failed' || job.status === 'stopped') {
        setAutoRefresh(false);
      } else if (job.status === 'in_progress' || job.status === 'pending' || job.status === 'running' || job.status === 'processing') {
        setAutoRefresh(true);
      }
    }
  }, [job?.status]);

  useEffect(() => {
    if (open && jobId && autoRefresh && (job?.status === 'in_progress' || job?.status === 'pending' || job?.status === 'running' || job?.status === 'processing')) {
      const intervalMs = refreshInterval * 1000;
      const interval = setInterval(() => {
        fetchJob();
      }, intervalMs);

      return () => clearInterval(interval);
    }
  }, [open, jobId, job?.status, autoRefresh, refreshInterval]);

  useEffect(() => {
    // Auto-scroll to bottom when log updates
    if (logEndRef.current && open) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [job?.error_message, open]);

  const fetchJob = async () => {
    if (!jobId) return;
    
    try {
      setLoading(true);
      const data = await contactService.getImportJob(jobId, clientId);
      setJob(data);
    } catch (err: any) {
      console.error('Failed to fetch job:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
      case 'running':
      case 'processing':
        return 'info';
      case 'failed':
        return 'error';
      case 'stopped':
        return 'warning';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  const handleCopyLog = async () => {
    if (!job?.error_message) return;
    
    try {
      await navigator.clipboard.writeText(job.error_message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy log to clipboard', err);
    }
  };


  const formatLog = (log: string | null | undefined): string => {
    if (!log) return '';
    return log;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Import Job Log {jobId ? `#${jobId}` : ''}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {job && (
              <Chip
                label={job.status}
                color={getStatusColor(job.status) as any}
                size="small"
              />
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Job Info */}
          {job && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Filename</Typography>
                  <Typography variant="body2">{job.filename}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Tag</Typography>
                  <Typography variant="body2">{job.tag_name}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Progress</Typography>
                  <Typography variant="body2">
                    {job.processed_rows} / {job.total_rows} ({job.total_rows > 0 ? Math.round((job.processed_rows / job.total_rows) * 100) : 0}%)
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Created</Typography>
                  <Typography variant="body2">{job.created_count}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Updated</Typography>
                  <Typography variant="body2">{job.updated_count}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Auto-refresh Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                />
              }
              label="Auto-refresh"
            />
            {autoRefresh && (
              <TextField
                type="number"
                value={refreshInterval}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val > 0 && val <= 300) {
                    setRefreshInterval(val);
                  }
                }}
                size="small"
                inputProps={{ min: 1, max: 300 }}
                sx={{ width: '80px' }}
              />
            )}
            <Typography variant="caption">sec</Typography>
            <IconButton
              size="small"
              onClick={fetchJob}
              disabled={loading}
              title="Manual refresh"
            >
              <RefreshIcon />
            </IconButton>
            {job?.error_message && (
              <IconButton
                size="small"
                onClick={handleCopyLog}
                title="Copy log to clipboard"
              >
                {copied ? <ContentCopyIcon color="success" /> : <ContentCopyIcon />}
              </IconButton>
            )}
          </Box>

          {/* Log Content */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              bgcolor: 'grey.900',
              color: 'grey.100',
              p: 2,
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {loading ? (
              <Typography>Loading...</Typography>
            ) : job?.error_message ? (
              <>
                {formatLog(job.error_message)}
                <div ref={logEndRef} />
              </>
            ) : (
              <Typography color="text.secondary">No log entries yet...</Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportJobLogViewer;





